import { useEffect, useState } from 'react'

import { BranchModel, type BranchOption } from '@/http/models/Branch'
import type { ResourceApi } from '@/http/models/Resource'
import { type Terminal, TerminalModel } from '@/http/models/Terminal'

import { Badge } from '@/components/ui/badge'

import { useAuth } from '@/contexts/AuthContext'
import { LiveTerminals } from '@/components/features/pos/LiveTerminals'
import { ResourcePage } from '@/components/features/pos/ResourcePage'

const api: ResourceApi<Terminal> = { getList: TerminalModel.getPage, create: TerminalModel.create, update: TerminalModel.update, remove: TerminalModel.remove }

/**
 * Tills of every branch (checklist C23). The number is assigned by the server and goes into in-store barcodes, so
 * it is shown but never edited. Paired devices are under Settings → POS → Till devices. No cash box column: devices
 * stopped sending it (D25); the live table above shows the open shift's drawer.
 */
const Terminals = () => {
  const [branches, setBranches] = useState<BranchOption[]>([])
  const canListBranches = useAuth().can('branches:list')

  useEffect(() => {
    if (!canListBranches) return
    BranchModel.getOptions().then((res) => {
      if (res.success && res.data) setBranches(res.data.result)
    })
  }, [canListBranches])

  return (
    <div className="space-y-8">
    <LiveTerminals branches={branches} />
    <ResourcePage<Terminal>
      title="Manage terminals"
      description="Add, rename or deactivate the terminals (counters) at each branch."
      noun="terminal"
      api={api}
      permissions={{ create: 'terminals:create', update: 'terminals:update', delete: 'terminals:delete' }}
      searchPlaceholder="Search by name"
      graph={{ _id: true, name: true, unique_no: true, branch_id: true, description: true, status: true, is_open: true, branch: { name: true } }}
      columns={[
        { label: 'No.', render: (t) => <span className="tabular-nums text-muted-foreground">{t.unique_no}</span> },
        { label: 'Name', render: (t) => <span className="font-medium text-ink">{t.name}</span> },
        { label: 'Branch', render: (t) => t.branch?.name ?? '—' },
        { label: 'Shift', render: (t) => <Badge tone={t.is_open ? 'success' : 'soft'}>{t.is_open ? 'Open' : 'Closed'}</Badge> },
        { label: 'Status', render: (t) => <Badge tone={t.status === 'inactive' ? 'soft' : 'success'}>{t.status === 'inactive' ? 'Inactive' : 'Active'}</Badge> },
      ]}
      fields={[
        { name: 'name', label: 'Name', type: 'text', required: true, placeholder: 'Counter 1' },
        { name: 'branch_id', label: 'Branch', type: 'select', required: true, options: branches.map((b) => ({ label: b.name, value: b._id })) },
        { name: 'description', label: 'Description', type: 'textarea' },
        { name: 'status', label: 'Active', type: 'status' },
      ]}
      toForm={(t) => ({
        name: t?.name ?? '',
        branch_id: t?.branch_id ?? (branches.length === 1 ? branches[0]._id : ''),
        description: t?.description ?? '',
        status: (t?.status ?? 'active') !== 'inactive',
      })}
    />
    </div>
  )
}

export default Terminals
